import { AIRPORT_CODE_PATTERN } from './patterns';
import type {
    AirspaceObstacle,
    AirspaceRestriction,
    NormalizeAirspaceSummary,
    Severity
} from './types';

const isString = (value: unknown): value is string => typeof value === 'string';

const isNumber = (value: unknown): value is number =>
    typeof value === 'number' && !Number.isNaN(value);

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const SEVERITY_ALIASES: Record<string, Severity> = {
    advisory: 'advisory',
    critical: 'critical',
    info: 'info',
    prohibited: 'prohibited',
    restricted: 'warning',
    warning: 'warning'
};

const mapSeverity = (raw: unknown, fallback: Severity): Severity => {
    if (!isString(raw)) {
        return fallback;
    }

    return SEVERITY_ALIASES[raw] ?? fallback;
};

// Backend v0.1 uses {data_type, lower_altitude_ft, upper_altitude_ft, altitude_ref, description}
// and v1 (per airspace §2.5) will use {layer, lower_ft, upper_ft, alt_ref, properties.full_text}.
// Normalize both to the same camelCase shape so cards render correctly during the rollout.
const LAYER_BY_DATA_TYPE: Record<string, string> = {
    advisory: 'advisory',
    airspace_class: 'class_c',
    notam_multitime: 'tfr',
    nsufr: 'nsufr',
    obstacle: 'obstacle',
    obstruction: 'obstacle',
    powerline: 'powerline',
    sfra: 'sfra',
    special_use_airspace: 'sua',
    stadium: 'stadium',
    sua: 'sua',
    tfr: 'tfr'
};

const resolveLayer = (record: Record<string, unknown>): string => {
    if (isString(record.layer) && record.layer.length > 0) {
        return record.layer;
    }

    if (isString(record.data_type) && record.data_type.length > 0) {
        return LAYER_BY_DATA_TYPE[record.data_type] ?? record.data_type;
    }

    return 'unknown';
};

const normalizeRestriction = (raw: unknown, fallbackSeverity: Severity): AirspaceRestriction => {
    const record = asRecord(raw);
    const requiredAuthorizations = Array.isArray(record.required_authorizations)
        ? record.required_authorizations.filter(isString)
        : [];
    const lowerFt = isNumber(record.lower_ft)
        ? record.lower_ft
        : isNumber(record.lower_altitude_ft)
          ? record.lower_altitude_ft
          : null;
    const upperFt = isNumber(record.upper_ft)
        ? record.upper_ft
        : isNumber(record.upper_altitude_ft)
          ? record.upper_altitude_ft
          : null;
    const altRef = isString(record.alt_ref)
        ? record.alt_ref
        : isString(record.altitude_ref)
          ? record.altitude_ref
          : null;
    const description = isString(record.description) ? record.description : null;
    const mergedProperties: Record<string, unknown> = {
        ...asRecord(record.properties),
        ...(description ? { full_text: description } : {})
    };

    return {
        altRef,
        dataLastSyncedAt: isString(record.data_last_synced_at) ? record.data_last_synced_at : null,
        distanceM: isNumber(record.distance_m) ? record.distance_m : null,
        effectiveEnd: isString(record.effective_end) ? record.effective_end : null,
        effectiveStart: isString(record.effective_start) ? record.effective_start : null,
        layer: resolveLayer(record),
        lowerFt,
        name: isString(record.name) ? record.name : 'Unknown',
        properties: mergedProperties,
        requiredAuthorizations,
        severity: mapSeverity(record.severity, fallbackSeverity),
        sourceId: isString(record.source_id) ? record.source_id : null,
        upperFt
    };
};

const normalizeObstacle = (raw: unknown): AirspaceObstacle => {
    const record = asRecord(raw);

    return {
        amslFt: isNumber(record.amsl_ft) ? record.amsl_ft : null,
        dataLastSyncedAt: isString(record.data_last_synced_at) ? record.data_last_synced_at : null,
        distanceM: isNumber(record.distance_m) ? record.distance_m : null,
        heightFt: isNumber(record.height_ft) ? record.height_ft : 0,
        lighting: isString(record.lighting) ? record.lighting : null,
        marking: isString(record.marking) ? record.marking : null,
        obstacleType: isString(record.obstacle_type) ? record.obstacle_type : 'Unknown',
        sourceId: isString(record.source_id) ? record.source_id : null
    };
};

const normalizeAirspaceSummary: NormalizeAirspaceSummary = (raw, clickedPoint) => {
    const record = asRecord(raw);
    const point = asRecord(record.point);
    const summary = asRecord(record.summary);

    // Filter out airspace classes that are outside drone operating altitude (max 400 ft AGL):
    //   - Class A: starts at FL180 (18,000 ft MSL)
    //   - Class E4: transition areas, starts at 700 ft AGL
    //   - Class E5: domestic Class E, starts at 1,200 ft AGL
    //   - Class E6: offshore airspace
    //   - Class E7: rarely-used high-altitude extension
    // Class B, C, D and the surface-area Class E variants (E2, E3) stay — those gate
    // drone operations under Part 107 §107.41.
    const DRONE_IRRELEVANT_CLASSES = new Set(['A', 'E4', 'E5', 'E6', 'E7']);
    const DRONE_IRRELEVANT_NAME_PATTERN = /\bclass\s+(a|e[4-7])\b/i;

    const isDroneRelevant = (restriction: AirspaceRestriction): boolean => {
        const klass = restriction.properties.class;

        if (typeof klass === 'string') {
            const normalized = klass.trim().toUpperCase();

            if (DRONE_IRRELEVANT_CLASSES.has(normalized)) {
                return false;
            }
        }

        return !DRONE_IRRELEVANT_NAME_PATTERN.test(restriction.name);
    };

    // Blue LAANC cards signal "you're standing inside a LAANC grid — here's the
    // authorization ceiling for this point." When the pin is outside the grid, the
    // backend still returns nearby laanc_grid cells within its proximity radius, but
    // the operator doesn't need LAANC authorization at their pin. Drop any LAANC row
    // with a non-zero distance so those facilities flow through as regular controlled-
    // airport proximity cards (yellow "LAANC available") instead. Require strict 0 —
    // null would be "distance not computed" and that's not a containment signal.
    const isAtPointLaancRow = (r: AirspaceRestriction): boolean =>
        r.layer !== 'laanc_grid' || r.distanceM === 0;

    const prohibited = Array.isArray(summary.prohibited)
        ? summary.prohibited
              .map(item => normalizeRestriction(item, 'prohibited'))
              .filter(isDroneRelevant)
              .filter(isAtPointLaancRow)
        : [];
    const restricted = Array.isArray(summary.restricted)
        ? summary.restricted
              .map(item => normalizeRestriction(item, 'warning'))
              .filter(isDroneRelevant)
              .filter(isAtPointLaancRow)
        : [];
    const advisory = Array.isArray(summary.advisory)
        ? summary.advisory
              .map(item => normalizeRestriction(item, 'advisory'))
              .filter(isDroneRelevant)
              .filter(isAtPointLaancRow)
        : [];

    // Dedupe overlapping airspace classes per facility. If a facility publishes both
    // Class B and Class D at the same point (common — Class D carve-outs inside a Class B
    // TRACON), surface only the most restrictive; the stricter authorization covers both.
    const SEVERITY_RANK: Record<string, number> = {
        advisory: 2,
        critical: 4,
        info: 1,
        prohibited: 5,
        warning: 3
    };
    // Matches "<facility> class [A-G]" optionally followed by a digit suffix (E2, E3, E4, etc.)
    // so Class E2 surface areas dedupe alongside Class D and are absorbed by a sibling LAANC card.
    const CLASS_NAME_PATTERN = /^(.+?)\s+class\s+[a-g][0-9]?\b/i;

    const facilityKey = (restriction: AirspaceRestriction): string | null => {
        const match = restriction.name.match(CLASS_NAME_PATTERN);

        return match ? match[1].trim().toUpperCase() : null;
    };

    // Caller pre-filters to rows with a facility key, so the null branch is never
    // taken — keep the map-by-facility path only.
    const keepMostRestrictive = (list: AirspaceRestriction[]): AirspaceRestriction[] => {
        const byFacility = new Map<string, AirspaceRestriction>();

        for (const item of list) {
            const key = facilityKey(item);

            if (key === null) {
                continue;
            }

            const existing = byFacility.get(key);

            if (
                !existing ||
                (SEVERITY_RANK[item.severity] ?? 0) > (SEVERITY_RANK[existing.severity] ?? 0)
            ) {
                byFacility.set(key, item);
            }
        }

        return [...byFacility.values()];
    };

    // Dedupe within the combined airspace-class pool (across buckets, because the same
    // facility may surface in `prohibited` as Class B and `restricted` as Class D).
    const classRestrictions = [...prohibited, ...restricted, ...advisory].filter(
        r => facilityKey(r) !== null
    );
    const deduped = new Set(keepMostRestrictive(classRestrictions));
    const keepItem = (r: AirspaceRestriction): boolean => facilityKey(r) === null || deduped.has(r);

    // LAANC absorbs airspace-class cards at the same clicked point. The API returns
    // records within a 100m radius, so any airspace_class row alongside a laanc_grid
    // row is describing the same airport — e.g. "Aurora State (UAO)" + "AURORA CLASS D"
    // are one facility, and "McNary Fld (SLE)" + "SALEM CLASS D" + "SALEM CLASS E2" are
    // another. The LAANC card shows the code, badge, and authorization ceiling, which
    // covers everything the class card conveyed. City names and airport colloquial
    // names often don't share tokens (McNary vs Salem), so a name-based facility match
    // doesn't work — spatial co-location at the 100m query radius is the honest signal.
    const hasLaanc = [...prohibited, ...restricted, ...advisory].some(
        r => r.layer === 'laanc_grid'
    );

    // Airport-code dedupe + severity remap. Cards referring to the same airport code
    // (Aurora State (UAO) LAANC + Aurora State (UAO) nearby-airport advisory) collapse
    // to one. Keep the LAANC card when present; otherwise keep the first one seen.
    const airportCode = (restriction: AirspaceRestriction): string | null =>
        restriction.name.match(AIRPORT_CODE_PATTERN)?.[1] ?? null;
    const laancAirportCodes = new Set(
        [...prohibited, ...restricted, ...advisory]
            .filter(r => r.layer === 'laanc_grid')
            .map(r => airportCode(r))
            .filter((code): code is string => code !== null)
    );

    // Backend tags Class B/C/D/E2 airports with a "Controlled airport" prefix in the
    // description (per 2026-04-20_backend_airport-radius-widened-15nm.md). Uncontrolled
    // airports use "Nearby airport (uncontrolled)". We read the prefix to decide color:
    // controlled → warning (yellow), uncontrolled → info (gray).
    const CONTROLLED_PREFIX_PATTERN = /^controlled\b/i;
    const UNCONTROLLED_PATTERN = /\buncontrolled\b/i;

    const describeText = (r: AirspaceRestriction): string => {
        const subtitle = typeof r.properties.subtitle === 'string' ? r.properties.subtitle : '';
        const fullText = typeof r.properties.full_text === 'string' ? r.properties.full_text : '';

        return `${subtitle} ${fullText}`.trim();
    };

    const isControlledAirport = (r: AirspaceRestriction): boolean =>
        CONTROLLED_PREFIX_PATTERN.test(describeText(r));

    const isUncontrolledAirport = (r: AirspaceRestriction): boolean =>
        UNCONTROLLED_PATTERN.test(describeText(r));

    // Blue (advisory severity) is reserved exclusively for LAANC-grid cards — the
    // color signals "authorization available via the LAANC app," which is only true
    // for laanc_grid rows. Every other advisory-severity row (MOA, SUA, stadium,
    // wildlife area, non-LAANC controlled airport) demotes to warning so blue stays
    // unambiguous. Confirmed product decision — do not re-add exceptions without
    // an explicit Chris call.
    const applyAirportSeverity = (r: AirspaceRestriction): AirspaceRestriction => {
        if (r.layer === 'laanc_grid') {
            return { ...r, severity: 'advisory' };
        }

        if (airportCode(r) !== null && isControlledAirport(r)) {
            return { ...r, layer: 'airport_controlled', severity: 'warning' };
        }

        if (airportCode(r) !== null && isUncontrolledAirport(r)) {
            return { ...r, layer: 'airport_uncontrolled', severity: 'info' };
        }

        if (r.severity === 'advisory') {
            return { ...r, severity: 'warning' };
        }

        return r;
    };

    const isDuplicateAirport = (r: AirspaceRestriction): boolean => {
        const code = airportCode(r);

        if (code === null || r.layer === 'laanc_grid') {
            return false;
        }

        // A non-LAANC airport-code row (e.g., "Aurora State (UAO)" airport proximity)
        // is redundant with its sibling LAANC grid card. Drop it.
        return laancAirportCodes.has(code);
    };

    const keepAfterLaancMerge = (r: AirspaceRestriction): boolean => {
        if (!keepItem(r)) {
            return false;
        }

        if (hasLaanc && facilityKey(r) !== null) {
            return false;
        }

        return !isDuplicateAirport(r);
    };

    // Collapse multiple rows that refer to the same airport down to one card.
    // Two sources of duplication:
    //   1. LAANC grid: a single airport publishes 100s of 1km cells. A click near
    //      the airport can match dozens at once. Keep the cell with the highest
    //      `upperFt` ceiling so the card reflects the most permissive authorization.
    //   2. Proximity airports: the backend occasionally returns the same airport
    //      twice (different internal sources). Keep the first occurrence.
    const collapseByAirportCode = (list: AirspaceRestriction[]): AirspaceRestriction[] => {
        const seen = new Map<string, AirspaceRestriction>();
        const passthrough: AirspaceRestriction[] = [];

        for (const item of list) {
            const code = airportCode(item);

            if (code === null) {
                passthrough.push(item);
                continue;
            }

            const key = `${item.layer}:${code}`;
            const existing = seen.get(key);

            if (!existing) {
                seen.set(key, item);
                continue;
            }

            const incomingCeiling = item.upperFt ?? 0;
            const existingCeiling = existing.upperFt ?? 0;

            if (incomingCeiling > existingCeiling) {
                seen.set(key, item);
            }
        }

        return [...seen.values(), ...passthrough];
    };

    const dedupedProhibited = collapseByAirportCode(
        prohibited.filter(keepAfterLaancMerge).map(applyAirportSeverity)
    );
    const dedupedRestricted = collapseByAirportCode(
        restricted.filter(keepAfterLaancMerge).map(applyAirportSeverity)
    );
    const dedupedAdvisory = collapseByAirportCode(
        advisory.filter(keepAfterLaancMerge).map(applyAirportSeverity)
    );
    const obstacles = Array.isArray(summary.obstacles)
        ? summary.obstacles.map(normalizeObstacle)
        : [];

    // Clip airport-proximity rows at 10 nm. Beyond that the operator doesn't care —
    // noise, not signal. LAANC grid rows and airspace polygons are not clipped;
    // they represent active airspace at the point.
    const AIRPORT_MAX_METERS = 18520;
    const isAirportLayer = (r: AirspaceRestriction): boolean =>
        r.layer === 'airport' ||
        r.layer === 'airport_controlled' ||
        r.layer === 'airport_uncontrolled';
    const withinAirportRange = (r: AirspaceRestriction): boolean =>
        !isAirportLayer(r) || r.distanceM === null || r.distanceM <= AIRPORT_MAX_METERS;
    const finalProhibited = dedupedProhibited.filter(withinAirportRange);
    const finalRestricted = dedupedRestricted.filter(withinAirportRange);
    const finalAdvisory = dedupedAdvisory.filter(withinAirportRange);

    const canFly =
        typeof record.can_fly === 'boolean'
            ? record.can_fly
            : finalProhibited.length + finalRestricted.length === 0;

    const totalRestrictions =
        finalProhibited.length + finalRestricted.length + finalAdvisory.length;

    // When the location has no real airspace hits, synthesize a Class G "safe" advisory so
    // downstream cards render a uniform AirspaceRestriction instead of a bespoke ClearCard.
    // totalRestrictions is computed above so the safe card never inflates the count.
    if (
        finalProhibited.length === 0 &&
        finalRestricted.length === 0 &&
        finalAdvisory.length === 0 &&
        obstacles.length === 0
    ) {
        const safeAdvisory: AirspaceRestriction = {
            altRef: null,
            dataLastSyncedAt: null,
            distanceM: null,
            effectiveEnd: null,
            effectiveStart: null,
            layer: 'class_g',
            lowerFt: 0,
            name: 'Class G Uncontrolled',
            properties: {
                full_text:
                    'Operations permitted under Part 107. Maintain VLOS and altitude limits.',
                subtitle: 'No advisories in this area'
            },
            requiredAuthorizations: [],
            severity: 'safe',
            sourceId: null,
            upperFt: null
        };

        finalAdvisory.push(safeAdvisory);
    }

    const pointLat = clickedPoint?.lat ?? (isNumber(point.lat) ? point.lat : 0);
    const pointLng = clickedPoint?.lng ?? (isNumber(point.lng) ? point.lng : 0);

    return {
        address: isString(record.address) ? record.address : null,
        canFly,
        checkedAt: isString(record.checked_at) ? record.checked_at : new Date().toISOString(),
        point: {
            lat: pointLat,
            lng: pointLng
        },
        summary: {
            advisory: finalAdvisory,
            obstacles,
            prohibited: finalProhibited,
            restricted: finalRestricted
        },
        totalRestrictions
    };
};

export default normalizeAirspaceSummary;
